import React from "react"
import {Badge, Checkbox, Col, List, Modal, Row, Spin, Tag, Tooltip, Typography} from "antd";
import IRestResource from "model/interface/api/IRestResource";
import IRepositoryService from "model/interface/IRepositoryService";
import IViewSettingsStructure from "model/interface/dataStorage/view/settings/IViewSettingsStructure";
import {connect} from "react-redux";
import selectors from "../../../redux/selectors";
import IContentType from "../../../model/interface/dataStorage/IContentType";
import Text from "antd/es/typography/Text";
import ViewAction from "./ViewAction";
import {IActionResult} from "../../../model/service/dataStorage/ActionsService";
import ViewCustomFilters, {DefaultCustomFilters} from "./ViewCustomFilters";
import {IBaseViewProps} from "./ViewUnit";
import ViewPersonalEditButton from "./ViewPersonalEditButton";
import ViewEditButton from "./ViewEditButton";
import ActionButton from "../action/ActionButton";
import IAction, {ACTION_RESERVED_NAMES} from "../../../model/interface/dataStorage/IAction";
import IPresenter from "../../../model/interface/dataStorage/IPresenter";
import PresenterBuilder from "../../../views/dataStorage/PresenterBuilder";
import _ from "underscore";
import ViewTitle from "./ViewTitle";
import ViewSchemeActions from "./ViewSchemeActions";
import IRestServiceFilters from "../../../model/interface/api/IRestServiceFilters";
import {
    buildCompositeFieldName,
    COMPOSITE_FIELD_FIELDS,
    COMPOSITE_FIELD_TYPE
} from "../../../model/interface/dataStorage/IField";
import {AimOutlined, FilterOutlined, ReloadOutlined} from "@ant-design/icons";
import Button from "../../shared/button/Button";
import FilterEvaluationService from "../../../model/service/api/FilterEvaluationService";
import Map, {GeoPoint} from "../../shared/map/Map";
import {IAppState} from "../../../redux/store";
import IUser from "../../../model/interface/security/IUser";
import ViewFilterIsland from "./tools/ViewFilterIsland";
import LocaleText from "../settings/dictionary/LocaleText";

const COLORS = [
    '#ff0000',
    '#407294',
    '#ff80ed',
    '#0000ff',
    '#ffff00',
    '#990000',
    '#daa520',
    '#065535',
    '#6897bb',
    '#f08080',
    '#660066',
    '#40e0d0',
    '#101010',
]

interface IState {
    results: { [uuid: string]: Array<IRestResource> },
    total: number,
    loading: boolean,
    customizationDraft: IViewSettingsStructure | null,
    customization: IViewSettingsStructure,
    filters: { [contentType: string]: IRestServiceFilters | undefined }
    activeContentTypes: string[],
    customFilters?: DefaultCustomFilters,
    renderHiddenActionButton?: JSX.Element,
    renderCreateActionModal?: boolean,
    selectedPoint?: GeoPoint
    showFilters?: boolean
}

interface IProps extends IBaseViewProps {
    findOneByFullClassName: (fullClassName: string) => IRepositoryService
    findOneByContentType: (contentType: IContentType) => IRepositoryService,
    findContentTypeByUuid: (uuid: string) => IContentType,
    extractRouteParametersFromUrl: (url: string) => null | { id: number, parameters: { [name: string]: any } }
    user: IUser
}

class ViewMap extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            results: {} as { [id: number]: Array<IRestResource> },
            filters: {},
            loading: false,
            total: 0,
            customization: {
                items: null
            },
            customizationDraft: null,
            activeContentTypes: [] as number[],
            ...this.presetState(),
            customFilters: ViewCustomFilters.getDefault(props.settings.customFilters)
        }
    }

    componentDidMount() {
        this.load()
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if (prevProps.reload !== this.props.reload && this.props.reload) {
            this.refresh()
        }
    }

    load = () => {
        if (this.state.activeContentTypes.length === 0) {
            const contentType = this.props.viewUnit.contentTypes[0]
            this.setState({activeContentTypes: [contentType]}, this.loadAllActive)
        } else {
            this.loadAllActive()
        }
    }

    loadResults(contentType: IContentType): Promise<void> {
        const {findOneByContentType} = this.props
        const {customFilters, filters} = this.state
        this.setState({loading: true})

        return findOneByContentType(contentType).collectionList({
            limit: 0,
            filters: {
                ...(customFilters?.[contentType.fullClassName] || {}),
                ...(filters?.[contentType.fullClassName] || {})
            }
        }).then(({count, results}: any) => {
            this.setState(prevState => ({
                results: {...prevState.results, [contentType.uuid]: results},
                total: count,
                loading: false
            }))
        })
    }

    presetState() {
        const {settings, state} = this.props
        let previousState: any | IState = {}

        if (settings.mapSelectedContentTypes && state.mapSelectedContentTypes) {
            previousState.activeContentTypes = state.mapSelectedContentTypes
        }
        if (settings.mapSaveFilters && state.mapFilters) {
            previousState.filters = state.mapFilters
            if (Object.keys(previousState.filters).length) {
                previousState.showFilters = true
            }
        }
        return previousState
    }

    saveState() {
        const {settings, saveState, state} = this.props
        const {activeContentTypes, filters} = this.state
        let newState = {...state}
        if (settings.mapSaveFilters && filters) {
            newState.mapFilters = filters
        }
        if (settings.mapSelectedContentTypes) {
            newState.mapSelectedContentTypes = activeContentTypes
        }
        saveState({...newState}).then()
    }

    doAction = (result?: IActionResult) => {
        result && this.refresh()
        return new Promise<void>(resolve => resolve()) //TODO
    }

    onFinish = (result?: IActionResult): Promise<void> => {
        return Promise.resolve().then(() => {
            this.cancelSelection()
            if (this.props.onFinishAction) {
                return this.props.onFinishAction(result)
            } else {
                return this.refresh()
            }
        })
    }

    getPayload() {
        let payload = {} as any;
        payload.route = this.props.extractRouteParametersFromUrl(this.props.history.location.pathname)
        return payload;
    }

    getPresenter = (resource: IRestResource) => {
        const {findOneByContentType, viewUnit} = this.props;
        let presenter: null | IPresenter
        if (resource.contentType) {
            const service = findOneByContentType(this.getContentType(resource.contentType))
            presenter = service.getPresenter(viewUnit.options?.mapPresenter?.[resource.contentType] || '')
            if (!presenter) {
                presenter = service.getDefaultPresenter()
            }
            presenter.options = {...presenter.options}
            return PresenterBuilder.build(presenter, resource, presenter.options) || resource.id + ''
        }
        return '(bez názvu)'
    }

    getLabel(event: IRestResource) {
        return this.getPresenter(event)
    }

    showFilters = () => {
        this.setState(state => ({showFilters: !state.showFilters}))
    }

    buildPoints() {
        const {history, match, viewUnit} = this.props
        const events: GeoPoint[] = []
        this.state.activeContentTypes.forEach(uuid => {
            this.state.results[uuid]?.forEach(item => {
                const contentType = this.props.findContentTypeByUuid(uuid)
                const field = contentType.fields.find(f => f.type === COMPOSITE_FIELD_TYPE.COORDINATES)
                if (field && item[field.name]) {
                    const lat = item[field.name]?.[buildCompositeFieldName(field.name, COMPOSITE_FIELD_FIELDS.COORDINATES.LATITUDE)]
                    const long = item[field.name]?.[buildCompositeFieldName(field.name, COMPOSITE_FIELD_FIELDS.COORDINATES.LONGITUDE)]

                    long && lat && events.push({
                        popupTrigger: 'hover',
                        lat, long, color: this.getSettingsColor(contentType, item), popupRender: point => {
                            return <>
                                <Row onClick={e => e.stopPropagation()}>
                                    <Col span={24}>
                                        <Text ellipsis={true} className={'font-size-md h-100 width-100 d-block mb-2'}
                                              onClick={e => e?.stopPropagation()}>
                                            {this.getLabel({...item, contentType: contentType.uuid})}
                                        </Text>
                                    </Col>
                                    <Col span={24}>
                                        <Tag className={'d-block'} color={'success'} icon={<AimOutlined/>}>{lat}, {long}</Tag>
                                    </Col>
                                    <Col span={24} className={'mt-2'}>
                                        <Row gutter={[6, 6]}>
                                            {viewUnit.entryActions
                                                .filter(eA => contentType.actions.find(a => eA.action === a.uuid))
                                                .filter(viewAction => {
                                                    const action = _.findWhere(this.findActionContentType(viewAction.action).actions, {uuid: viewAction.action})
                                                    return action && item._permissions[action.name]
                                                })
                                                .map((action) => (
                                                    <Col>
                                                        <ViewAction
                                                            history={history}
                                                            match={match}
                                                            resources={[item]}
                                                            key={action.uuid}
                                                            action={action}
                                                            payload={this.getPayload()}
                                                            onFinish={this.onFinish}
                                                        />
                                                    </Col>
                                                ))}
                                        </Row>

                                    </Col>
                                </Row>
                            </>
                        }
                    })
                }
            })
        })
        return events
    }

    updateResults = (contentType: IContentType) => {
        this.setState(prevState => {
            const exists = this.state.activeContentTypes.includes(contentType.uuid)
            return exists ? {activeContentTypes: prevState.activeContentTypes.filter(type => type !== contentType.uuid)} :
                {activeContentTypes: [...prevState.activeContentTypes, contentType.uuid]}
        }, () => {
            this.loadResults(contentType).then()
            this.saveState()
        })
    }

    selectAll = () => {
        this.setState({
            activeContentTypes: !this.isCheckedAll() ? this.props.viewUnit.contentTypes.map(type => type) : []
        })
        this.loadAll();
    }

    loadAll() {
        this.props.viewUnit.contentTypes.forEach(contentType => {
            this.loadResults(this.getContentType(contentType)).then()
        })
    }

    loadActive() {
        this.state.activeContentTypes.forEach(contentTypeId => {
            this.loadResults(this.getContentType(contentTypeId)).then()
        })
    }

    getContentType(uuid: string) {
        return this.props.findContentTypeByUuid(uuid)
    }

    refresh = () => {
        this.setState({results: {}}, this.loadActive)
    }

    isCheckedAll() {
        return this.state.activeContentTypes.length === this.props.viewUnit.contentTypes.length;
    }

    onSelect = (point: GeoPoint) => {
        const {viewUnit} = this.props
        if (viewUnit.contentTypes.length > 1) {
            this.setState({renderCreateActionModal: true, selectedPoint: point})
        } else {
            this.setState({
                renderHiddenActionButton: this.getActionButton(point, this.getContentType(viewUnit.contentTypes[0]), true)
            })
        }
    }

    getActionButton(point: GeoPoint, contentType: IContentType, trigger = false) {
        const actions = contentType.actions
        const action = this.getCreateAction(actions);
        const field = contentType.fields.find(f => f.type === COMPOSITE_FIELD_TYPE.COORDINATES)
        return action && field && this.props.permissions[contentType.fullClassName] && this.props.permissions[contentType.fullClassName][action.name] ?
            <ActionButton
                options={{
                    label: '',
                    icon: 'antd.PlusOutlined',
                    type: 'info'
                }}
                onClick={(execute) => {
                    this.setState({loading: true})
                    return execute()
                }}
                key={action.uuid} action={action}
                payload={{
                    data: {
                        [field.name]: {
                            [buildCompositeFieldName(field.name, COMPOSITE_FIELD_FIELDS.COORDINATES.LATITUDE)]: point.lat,
                            [buildCompositeFieldName(field.name, COMPOSITE_FIELD_FIELDS.COORDINATES.LONGITUDE)]: point.long
                        }
                    }
                }}
                onFinish={this.onFinish}
                triggerClickRender={trigger}
            /> : <></>
    }

    getCreateAction(actions: IAction[]) {
        return actions.find(a => [ACTION_RESERVED_NAMES.MAP_CREATE].includes(a.name)) || actions.find(a => ['create'].includes(a.name));
    }

    loadAllActive() {
        this.state.activeContentTypes.forEach(uuid => {
            this.loadResults(this.props.findContentTypeByUuid(uuid)).then()
        });
    }

    getSettingsColor(contentType: IContentType, resource?: IRestResource) {
        const {settings, user} = this.props
        const conditionalColors = settings.conditionalColors?.[contentType.fullClassName]
        if (conditionalColors && resource) {
            let color = ''
            const found = conditionalColors.some(function (c) {
                color = c.color
                return !c.conditions || Object.keys(c.conditions).length === 0 || FilterEvaluationService.evaluate(c.conditions, resource, contentType, user)
            })
            return found ? color : undefined
        }
        return settings.colors?.[contentType.fullClassName]
    }

    onCustomFilterChange = (filters?: DefaultCustomFilters) => {
        this.setState({customFilters: filters}, this.refresh)
    }

    onFilterChange = (filters: IRestServiceFilters, contentType: string) => {
        this.setState(state => ({
            filters: {
                ...state.filters, [contentType]: {...state.filters[contentType], ...filters}
            }
        }), () => {
            this.saveState()
            this.refresh()
        })
    }

    findActionContentType(uuid: string) {
        return this.getContentType(
            this.props.viewUnit.contentTypes
                .find(c => this.getContentType(c).actions.find(a => a.uuid === uuid)) || '')
    }

    cancelSelection = () => {
        this.setState({
            renderCreateActionModal: false,
            renderHiddenActionButton: undefined
        })
    }

    clearFilters = () => {
        this.setState({filters: {}}, () => {
            this.refresh()
        })
    }

    render() {
        const {viewUnit, settings, history, match, permissions, insideTab, standalone} = this.props
        const {
            loading,
            activeContentTypes,
            renderHiddenActionButton,
            renderCreateActionModal,
            filters,
            showFilters,
            selectedPoint
        } = this.state;

        return (
            <>
                <Row justify={"space-between"}>
                    <ViewTitle hideTitle={insideTab || !standalone} viewUnit={viewUnit}>
                        <Row className={"noPrint"} gutter={[4, 6]}>
                            {activeContentTypes.map(contentType => {
                                const contentTypeObject = this.getContentType(contentType)
                                return <ViewSchemeActions onFinish={this.onFinish} contentType={contentTypeObject}
                                                          permissions={permissions} viewUnit={viewUnit} match={match}
                                                          history={history}/>
                            })}
                            <Col>
                                <Tooltip title={<LocaleText code={'view.reload'} fallback={"Znovu načíst data"}/>}>
                                    <Button
                                        onClick={() => this.refresh()}
                                        style={{verticalAlign: "middle"}}
                                        icon={<ReloadOutlined/>}
                                        size={"small"}
                                    />
                                </Tooltip>
                            </Col>
                            <ViewPersonalEditButton {...this.props}/>
                            <ViewEditButton {...this.props}/>
                            {Object.values(settings.filterIsland || {}).some(e => e.some(d => d.enabled && !d.defaultVisible)) && (
                                <Row>
                                    <Col>
                                        <Button type={Object.keys(filters).length ? 'info' : undefined} size={"small"}
                                                onClick={this.showFilters}
                                                icon={<FilterOutlined/>}/>
                                    </Col>
                                    {Object.keys(filters).length > 0 && <Col>
                                        <Button type={"link"} size={"small"} onClick={this.clearFilters}>
                                            <LocaleText code={'view.filters.cancel'} fallback={'Zrušit filtry'}/>
                                        </Button>
                                    </Col>}
                                </Row>
                            )}
                        </Row>
                    </ViewTitle>
                </Row>
                <Row>
                    {settings.customFilters && (
                        <Col>
                            <ViewCustomFilters filters={settings.customFilters}
                                               onChange={this.onCustomFilterChange}/>
                        </Col>
                    )}
                </Row>

                {viewUnit.contentTypes.map((contentType, index) => {
                    const contentTypeObject = this.getContentType(contentType)
                    const filterItems = settings.filterIsland?.[contentTypeObject.uuid]
                      return filterItems ? <div key={index}><ViewFilterIsland collapsed={!showFilters} filters={filterItems} defaultValues={filters?.[contentTypeObject.fullClassName] || {}} viewUnit={viewUnit} onChange={this.onFilterChange} contentType={contentTypeObject}/></div> : <></>
                })}


                <Row gutter={20} className={'flex-grow-1'}>
                    {viewUnit.contentTypes.length > 1 && (
                        <Col xs={24} xxl={4}>
                            <List
                                header={(
                                    <Typography.Title className={'m-0'} level={4}>
                                        <Row justify={"space-between"}>
                                            <Text>Typy obsahu</Text>
                                            <Checkbox checked={this.isCheckedAll()} onChange={this.selectAll}
                                                      className={'ml-2'}/>
                                        </Row>
                                    </Typography.Title>
                                )}>
                                {viewUnit.contentTypes.map((contentType, index) => {
                                    const contentTypeObject = this.getContentType(contentType)
                                    const color = this.getSettingsColor(contentTypeObject)
                                    return (
                                        <List.Item key={index} onClick={() => this.updateResults(contentTypeObject)}
                                                   className={"cursor-pointer"}>
                                            <Badge color={color ? color : COLORS[index]}
                                                   text={contentTypeObject.label || contentTypeObject.name}/>
                                            <Checkbox checked={activeContentTypes.includes(contentType)}/>
                                        </List.Item>
                                    )
                                })}
                            </List>
                        </Col>
                    )}
                    <Col xxl={viewUnit.contentTypes.length > 1 ? 20 : 24} xs={24}>
                        <Spin spinning={loading} wrapperClassName={'h-100'}>
                            <Map points={this.buildPoints()} height={'100%'} onClick={this.onSelect}/>
                        </Spin>
                    </Col>
                </Row>
                <div hidden={true}>
                    {renderHiddenActionButton}
                </div>
                {renderCreateActionModal && selectedPoint && (
                    <Modal visible={true} title={'Vyberte, ve které agendě chcete záznam vytvořit'}
                           cancelText={'zrušit'}
                           onCancel={this.cancelSelection}
                           okButtonProps={{hidden: true}}>
                        <Row>
                            {viewUnit.contentTypes.map(contentType => {
                                const contentTypeObject = this.getContentType(contentType)
                                const button = this.getActionButton(selectedPoint, contentTypeObject)
                                return !!button && (
                                    <Col className={'border-bottom w-100 py-2'} key={contentType}>
                                        <Row justify={"space-between"} className={'w-100'} align={'middle'}>
                                            <Typography.Text strong>
                                                {contentTypeObject.label || contentTypeObject.name}
                                            </Typography.Text>
                                            {button}
                                        </Row>
                                    </Col>
                                )
                            })}
                        </Row>
                    </Modal>
                )}
            </>
        )
    }
}


const mapStateToProps = (state: IAppState) => {
    return {
        user: state.setup.user,
        findOneByContentType: (contentType: IContentType) => selectors.services.findOneByContentType(state, contentType),
        findOneByFullClassName: (fullClassName: string) => selectors.services.findOneByFullClassName(state, fullClassName),
        findContentTypeByUuid: (uuid: string) => selectors.contentTypes.findOneBy(state, 'uuid', uuid),
        extractRouteParametersFromUrl: (url: string) => selectors.routes.extractRouteParametersFromUrl(state, url)
    }
}

export default connect(mapStateToProps)(ViewMap)